import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Target, Eye, TrendingUp, ArrowRight, Sun, Moon } from 'lucide-react';
import QuickAnswer from '../components/QuickAnswer';

const DailyPractice = () => {
  return (
    <div className="bg-gradient-to-b from-[#e8f4fd] to-[#f0f8ff] min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center">
          Your Daily Personal Growth Practice
        </h1>
        <p className="text-xl text-gray-600 text-center mb-10">
          The Levanta Cycle — the 10-minute loop that keeps growth moving.
        </p>

        <QuickAnswer label="The daily practice in one sentence">
          Levanta turns personal development into a daily practice through a
          four-step cycle — Learning, Practice, Reflection and Tracking — that
          takes roughly 10 minutes a day and keeps growth alive even when
          motivation is gone.
        </QuickAnswer>

        <div className="space-y-8">
          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Why a daily practice (and not a weekend course)
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Weekend courses and binge-consumed content create a burst of
              clarity that evaporates within 72 hours. The research is brutal:
              almost everything you learn in a single intense session is gone
              within a week unless you practice it.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Levanta inverts the model. Instead of big effort rarely, you do
              small effort daily — and compounding takes over.
            </p>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              The Levanta Cycle
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-blue-50 rounded-2xl p-5">
                <BookOpen className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Learning</h3>
                <p className="text-sm text-gray-700">
                  A short, focused lesson built for 2–5 minutes. Concept first,
                  no filler.
                </p>
              </div>
              <div className="bg-green-50 rounded-2xl p-5">
                <Target className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Practice</h3>
                <p className="text-sm text-gray-700">
                  A single real-world action for today. Applied, not theoretical.
                </p>
              </div>
              <div className="bg-purple-50 rounded-2xl p-5">
                <Eye className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Reflection</h3>
                <p className="text-sm text-gray-700">
                  A quick prompt on what happened and what you noticed.
                </p>
              </div>
              <div className="bg-orange-50 rounded-2xl p-5">
                <TrendingUp className="w-6 h-6 text-orange-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Tracking</h3>
                <p className="text-sm text-gray-700">
                  Visible progress on the skill you&apos;re building, compounded
                  over weeks.
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              A realistic day with Levanta
            </h2>
            <div className="space-y-5">
              <div className="flex gap-4">
                <Sun className="w-8 h-8 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Morning (2 minutes)</p>
                  <p className="text-gray-700 text-sm">
                    Open Levanta. Read the lesson. Pick the practice for today.
                    That&apos;s it.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Target className="w-8 h-8 text-blue-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">During the day</p>
                  <p className="text-gray-700 text-sm">
                    Do the one practice when the real-life trigger appears —
                    active listening in a meeting, a boundary conversation at
                    dinner, a mindful set at the gym.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Moon className="w-8 h-8 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Evening (3 minutes)</p>
                  <p className="text-gray-700 text-sm">
                    Reflect on what happened. Mark progress. Close the loop.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-6">
              Total: roughly 10 minutes a day — but built into the day so it
              never competes with it.
            </p>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              What about the bad days?
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Bad days are when every system gets tested. The Levanta rule is
              simple: <strong>never miss twice</strong>. If you miss one day,
              that&apos;s life. If you miss two days in a row, that&apos;s a new
              habit forming in the wrong direction.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The{' '}
              <Link to="/community" className="text-blue-600 hover:underline">
                Community
              </Link>{' '}
              is the environment that catches you before the second miss.
            </p>
          </Card>
        </div>

        <div className="text-center mt-12">
          <Link to="/download-app">
            <Button
              data-testid="daily-practice-cta"
              className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-5 text-lg rounded-xl shadow-md"
            >
              Start your daily practice
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DailyPractice;
